import React, { useMemo, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Sparkles, X, Send } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { platformAPI } from '../../services/api';

// Persists across remounts so the bubble only shows once per page session
let _bubbleDismissed = false;

const C = {
  primary: '#012d1d',
  accent: '#c1ecd4',
  white: '#ffffff',
  surface: '#fbf9f5',
  surfaceLow: '#f5f3ef',
  text: '#1b1c1a',
  muted: '#414844',
  mutedLight: '#717973',
  outlineVar: '#c1c8c2',
};

const systemWelcome = 'Ask anything about your current building analytics, services, and data exchange.';

/**
 * Floating multilingual assistant widget with page-context injection.
 * Redesigned with sparkle FAB + expanding label + glass chat panel.
 */
export const FloatingAssistant = ({ activeService, buildingId }) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [showBubble, setShowBubble] = useState(!_bubbleDismissed);

  useEffect(() => {
    if (_bubbleDismissed) return;
    const timer = setTimeout(() => {
      _bubbleDismissed = true;
      setShowBubble(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([
    { id: 'welcome', role: 'assistant', text: systemWelcome },
  ]);
  const [loading, setLoading] = useState(false);

  const location = useLocation();
  const { i18n } = useTranslation();
  const { primaryRole } = useAuth();

  const context = useMemo(
    () => ({
      pagePath: location.pathname,
      buildingId: buildingId || null,
      activeService: activeService || null,
      userRole: primaryRole,
    }),
    [location.pathname, buildingId, activeService, primaryRole]
  );

  const sendMessage = async () => {
    const message = input.trim();
    if (!message) return;

    setMessages((current) => [
      ...current,
      { id: `u-${Date.now()}`, role: 'user', text: message },
    ]);
    setInput('');
    setLoading(true);

    try {
      const response = await platformAPI.askAssistant({
        message,
        language: i18n.language,
        context,
      });
      setMessages((current) => [
        ...current,
        { id: `a-${Date.now()}`, role: 'assistant', text: response.response },
      ]);
    } catch (error) {
      toast.error(error.message || 'Assistant request failed');
    } finally {
      setLoading(false);
    }
  };

  const widget = (
    <div style={{ position: 'fixed', bottom: 40, right: 40, zIndex: 60, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 16 }}>

      {/* ── Chat Panel ── */}
      {open && (
        <div style={{
          width: 380, maxHeight: 520, background: C.white, borderRadius: 20,
          boxShadow: '0 24px 80px rgba(1,45,29,0.18)', border: `1px solid ${C.outlineVar}30`,
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          animation: 'fadeInUp 0.25s ease-out',
        }}>
          {/* Header */}
          <div style={{
            padding: '16px 20px', background: C.primary, color: C.white,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Sparkles size={20} color={C.accent} />
              <span style={{ fontWeight: 700, fontSize: 15 }}>LiveBetter Assistant</span>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close assistant"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.accent, padding: 4 }}
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12,
          }}>
            {messages.map((message) => (
              <div key={message.id} style={{
                alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
                background: message.role === 'user' ? C.primary : C.surfaceLow,
                color: message.role === 'user' ? C.white : C.text,
                padding: '10px 16px', borderRadius: 14, maxWidth: '85%',
                fontSize: 13, lineHeight: 1.6,
                borderBottomRightRadius: message.role === 'user' ? 4 : 14,
                borderBottomLeftRadius: message.role === 'assistant' ? 4 : 14,
              }}>
                {message.text}
              </div>
            ))}
            {loading && (
              <div style={{
                alignSelf: 'flex-start', background: C.surfaceLow, color: C.muted,
                padding: '10px 16px', borderRadius: 14, fontSize: 13, borderBottomLeftRadius: 4,
              }}>
                Thinking...
              </div>
            )}
          </div>

          {/* Input */}
          <div style={{
            padding: '12px 16px', borderTop: `1px solid ${C.outlineVar}30`,
            display: 'flex', gap: 8, alignItems: 'center',
          }}>
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Enter') sendMessage(); }}
              aria-label="Assistant prompt"
              placeholder="Type your question..."
              style={{
                flex: 1, border: 'none', background: C.surfaceLow, borderRadius: 10,
                padding: '10px 14px', fontSize: 13, outline: 'none', color: C.text,
              }}
            />
            <button
              type="button"
              onClick={sendMessage}
              disabled={loading}
              style={{
                width: 38, height: 38, borderRadius: 10, background: C.primary,
                border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center',
                justifyContent: 'center', opacity: loading ? 0.5 : 1,
                transition: 'opacity 0.15s',
              }}
            >
              <Send size={16} color={C.white} />
            </button>
          </div>
        </div>
      )}

      {/* ── Suggestion bubble (only when chat is closed, auto-hides after 5s) ── */}
      {!open && showBubble && (
        <div style={{
          background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)', border: `1px solid ${C.outlineVar}4d`,
          borderRadius: 16, padding: '12px 16px',
          boxShadow: '0 12px 40px rgba(1,45,29,0.12)', maxWidth: 280,
          animation: 'fadeInUp 0.3s ease-out',
        }}>
          <p style={{ fontSize: 12, fontWeight: 500, color: C.primary, lineHeight: 1.5, margin: 0 }}>
            Need help? Ask me about building analytics, services, or platform features.
          </p>
        </div>
      )}

      {/* ── FAB Button ── */}
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        aria-label="Toggle virtual assistant"
        style={{
          display: 'flex', alignItems: 'center', gap: 12,
          background: C.primary, color: C.white, border: 'none',
          padding: '16px 20px', height: 56, borderRadius: 9999, cursor: 'pointer',
          boxShadow: '0 12px 48px rgba(1,45,29,0.3)',
          transform: hovered ? 'scale(1.05)' : 'scale(1)',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        }}
      >
        <Sparkles size={24} color={C.accent} />
        <span style={{
          maxWidth: hovered || open ? 200 : 0,
          overflow: 'hidden', whiteSpace: 'nowrap',
          transition: 'max-width 0.3s ease-out',
          fontWeight: 700, fontSize: 14, paddingRight: hovered || open ? 4 : 0,
        }}>
          Ask Assistant
        </span>
      </button>

      {/* ── Inline keyframes ── */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );

  return createPortal(widget, document.body);
};

